import { Component, Input, Output, EventEmitter } from '@angular/core';
import { BaseComponent } from '../../../../base/base.component';
import { DetailItemComponent } from '../../../../core/panels/detail-panel/detail-item.component';
import { DetailPanelComponent } from '../../../../core/panels/detail-panel/detail-panel.component';
import { EmptyStateComponent } from '../../../../core/panels/empty-state.component';
import { ProgressbarComponent } from '../../../../core/labels/progressbar.component';
import { ButtonComponent } from '../../../../core/buttons/button.component';
import { OshaData } from '../../../../../models/api/view/claim/details/workers-compensation/osha-data';

@Component({
  selector: 'app-osha-wc',
  imports: [
    DetailPanelComponent,
    DetailItemComponent,
    EmptyStateComponent,
    ProgressbarComponent,
    ButtonComponent,
  ],
  template: `
    @if (isLoading) {
      <app-empty-state
        height="400px"
        icon="fa-light fa-file-arrow-down"
        label="Loading OSHA data"
        message="Retrieving OSHA information"
      >
        <div class="tw-py-4">
          <app-progressbar />
        </div>
      </app-empty-state>
    } @else if (hasError) {
      <app-empty-state
        height="400px"
        icon="fa-light fa-triangle-exclamation"
        label="Error loading OSHA data"
        message="An error occurred while retrieving OSHA information. Please try again."
      >
        <div class="tw-py-4">
          <app-button label="Retry" emphasis="High" (click)="onRetry()" />
        </div>
      </app-empty-state>
    } @else if (!oshaData) {
      <app-empty-state
        height="400px"
        icon="fa-light fa-file-exclamation"
        label="No OSHA data available"
        message="OSHA services are purchased, but no data has been recorded for this claim"
      />
    } @else {
      <div class="tw-flex tw-w-full tw-gap-16 tw-py-8">
        <app-detail-panel [dataSource]="dataSource" class="tw-basis-1/2">
          <app-detail-item
            label="OSHA location code"
            name="oshaLocationCode"
            labelCase="none"
          />
          <app-detail-item
            label="OSHA recordable"
            name="oshaRecordable"
            format="b"
            labelCase="none"
          />
          <app-detail-item
            label="Accident/Illness Code"
            name="accidentIllnessCode"
          >
            <ng-template #value>
              {{ accidentIllnessCodeWithDescription }}
            </ng-template>
          </app-detail-item>
          <app-detail-item
            label="Injury or Illness Work Related"
            name="injuryOrIllnessWorkRelated"
            format="b"
          />
          <app-detail-item
            label="Injury or Illness Resulted in Days Away from Work"
            name="daysOffWork"
            format="b"
          />
          <app-detail-item
            label="Injury or Illness Resulted in Restricted Work Activity"
            name="resultRestricted"
            format="b"
          />
          <app-detail-item
            label="Injury or Illness Resulted in Permanent Change in Job Duties or Transfer"
            name="resultedInPermanentChange"
            format="b"
          />
          <app-detail-item
            label="Medical Treatment Beyond First Aid"
            name="treatmentBeyondFirstAid"
            format="b"
          />
          <app-detail-item
            label="Employee Loss of Consciousness"
            name="lossOfConsciousness"
            format="b"
          />
          <app-detail-item
            label="Significant Injury or Illness Diagnosed"
            name="significantInjuryOrIllness"
            format="b"
          />
          <app-detail-item
            label="Did the injury or illness involve an exposure to BBP from a needle stick/cut/puncture"
            name="contaminatedNeedleInvolved"
            format="b"
            labelCase="none"
          />
          <app-detail-item
            label="Removed from Job Due to OSHA Health Standards"
            name="removedFromJob"
            format="b"
            labelCase="none"
          />
        </app-detail-panel>

        <app-detail-panel [dataSource]="dataSource" class="tw-basis-1/2">
          <app-detail-item
            label="Tested positive for TB exposure"
            name="testedPositiveForTb"
            format="b"
            labelCase="none"
          />
          <app-detail-item
            label="Threshold Shift in Hearing > 10 db and Hearing Level > 25 db"
            name="thresholdShiftInHearing"
            format="b"
          />
          <app-detail-item
            label="Employee death OSHA recordable"
            name="deathRecordable"
            labelCase="none"
          />
          <app-detail-item
            label="Employee Treated in the Emergency Room"
            name="treatedInEmergencyRoom"
            format="b"
          />
          <app-detail-item
            label="Employee Hospitalized Overnight as an In-Patient"
            name="hospitalizedOvernight"
            format="b"
          />
          <app-detail-item
            label="Time Employee Began Work"
            name="workBeginTime"
          />
          <app-detail-item label="Location of Case" name="caseLocation" />
          <app-detail-item
            label="Action Employee Performing When Injured"
            name="actionPerformingWhenInjured"
          />
          <app-detail-item
            label="Object/Substance Which Directly Injured Employee"
            name="injuryObject"
          />
          <app-detail-item label="Privacy Case" name="privacyCase" format="b" />
          <app-detail-item
            label="OSHA account"
            name="oshaAccount"
            labelCase="none"
          />
          <app-detail-item label="OSHA unit" name="oshaUnit" labelCase="none" />
        </app-detail-panel>
      </div>
    }
  `,
  styles: ``,
})
export class OshaWcComponent extends BaseComponent {
  @Input() oshaData: OshaData | null = null;
  @Input() isLoading = false;
  @Input() hasError = false;
  @Output() retry = new EventEmitter<void>();

  get dataSource() {
    return this.oshaData;
  }

  get accidentIllnessCodeWithDescription(): string {
    if (!this.oshaData) return '';

    const code = this.oshaData.accidentIllnessCode || '';
    const description = this.oshaData.accidentIllnessDescription || '';

    if (code && description) {
      return `${code} ${description}`;
    }

    return code || description;
  }

  onRetry(): void {
    this.retry.emit();
  }
}
